/**
 * Ad Slot Mapper
 * Normalizes slots from GET /api/ads into render-ready data.
 *
 * Besides the image, a slot may carry text content. The API doesn't fix names for
 * it yet, so the common aliases below are accepted, either on the slot itself or
 * inside a nested `content` object. Only plain text is used — HTML is never rendered.
 */
import { resolveApiUrl } from '../api/axiosClient';

export const AD_CONTENT_FIELDS = {
  headline: ['title', 'headline', 'heading'],
  description: ['description', 'body', 'text', 'subtitle', 'tagline', 'caption', 'message'],
  ctaLabel: ['cta_text', 'cta_label', 'cta', 'button_text', 'button_label', 'link_text'],
  sponsor: ['sponsor', 'sponsor_name', 'advertiser', 'advertiser_name', 'brand'],
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const toText = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text || null;
};

const pickText = (sources, keys) => {
  for (const source of sources) {
    for (const key of keys) {
      const text = toText(source[key]);
      if (text) return text;
    }
  }
  return null;
};

// Only http(s) URLs and site-relative paths; blocks javascript:, data:, protocol-relative, etc.
export const toSafeHref = (value) => {
  const text = toText(value);
  if (!text) return null;
  if (/^https?:\/\//i.test(text)) return text;
  if (text.startsWith('/') && !text.startsWith('//')) return text;
  return null;
};

const hostnameOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
};

export const mapAdSlot = (ad) => {
  if (!isObject(ad) || !ad.id) return null;

  const content = isObject(ad.content) ? ad.content : null;
  const sources = content ? [ad, content] : [ad];
  const cta = [ad.cta, content?.cta].find(isObject) || null;

  const href =
    toSafeHref(ad.href) ||
    toSafeHref(ad.url) ||
    toSafeHref(ad.link) ||
    toSafeHref(cta?.url) ||
    toSafeHref(cta?.href);
  const isExternal = Boolean(href && /^https?:\/\//i.test(href));

  const rawImage = toText(ad.preview_url) || toText(ad.image_url);
  const imageUrl = ad.has_image !== false && rawImage ? resolveApiUrl(rawImage) : null;

  const headline = pickText(sources, AD_CONTENT_FIELDS.headline);
  const description =
    pickText(sources, AD_CONTENT_FIELDS.description) || (content ? null : toText(ad.content));
  const ctaLabel =
    pickText(sources, AD_CONTENT_FIELDS.ctaLabel) || toText(cta?.text) || toText(cta?.label);

  return {
    id: String(ad.id),
    enabled: ad.enabled !== false,
    href,
    isExternal,
    domain: isExternal ? hostnameOf(href) : null,
    imageUrl,
    width: Number(ad.width) > 0 ? Number(ad.width) : null,
    height: Number(ad.height) > 0 ? Number(ad.height) : null,
    alt: toText(ad.alt) || headline || 'Advertisement',
    headline,
    description,
    ctaLabel,
    sponsor: pickText(sources, AD_CONTENT_FIELDS.sponsor),
    hasContent: Boolean(headline || description),
  };
};
